export const BAlert = {
  props: ['variant', 'show', 'dismissible'],
  template: `
    <div v-if="show !== false"
      class="alert mb-3"
      :class="{
        'alert-success': variant === 'success',
        'alert-error': variant === 'danger',
        'alert-warning': variant === 'warning',
        'alert-info': variant === 'info',
      }">
      <slot />
    </div>
  `
};

export const BButton = {
  props: ['variant', 'disabled', 'type', 'pill', 'size'],
  template: `
    <button
      :type="type || 'button'"
      :disabled="disabled"
      class="btn"
      :class="{
        'btn-primary': variant === 'primary',
        'btn-success': variant === 'success',
        'btn-error': variant === 'danger',
        'btn-warning': variant === 'warning',
        'btn-ghost': variant === 'secondary' || variant === 'light',
        'btn-neutral': variant === 'dark',
        'btn-sm': size === 'sm',
        'btn-lg': size === 'lg',
        'rounded-full': pill,
      }">
      <slot />
    </button>
  `
};

export const BModal = {
  props: ['modelValue', 'title', 'hideFooter', 'hideHeader',
    'centered', 'size', 'noCloseOnBackdrop'],
  emits: ['update:modelValue', 'show', 'hidden'],
  template: `
    <Transition name="modal">
      <div v-if="modelValue"
        class="fixed inset-0 z-[9999] flex items-center
        justify-center bg-black/50 p-4"
        @click.self="!noCloseOnBackdrop &&
          $emit('update:modelValue', false)">
        <div class="bg-base-100 rounded-2xl shadow-xl w-full
          max-h-[90vh] overflow-y-auto relative"
          :class="{
            'max-w-md': !size,
            'max-w-2xl': size === 'lg',
            'max-w-5xl': size === 'xl',
          }">
          <div v-if="!hideHeader"
            class="flex justify-between items-center p-5
            border-b border-base-300">
            <h3 class="font-bold text-lg">{{ title }}</h3>
            <button
              @click="$emit('update:modelValue', false)"
              class="btn btn-sm btn-circle btn-ghost">✕
            </button>
          </div>
          <div class="p-6"><slot /></div>
          <div v-if="!hideFooter"
            class="p-4 border-t border-base-300 flex
            justify-end gap-2">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  `
};

export const BForm = {
  emits: ['submit'],
  template: `
    <form @submit.prevent="$emit('submit', $event)">
      <slot />
    </form>
  `
};

export const BFormGroup = {
  props: ['label', 'labelFor', 'description'],
  template: `
    <div class="form-control mb-3">
      <label v-if="label" class="label">
        <span class="label-text font-medium">{{ label }}</span>
      </label>
      <slot />
      <label v-if="description" class="label">
        <span class="label-text-alt text-gray-400">
          {{ description }}
        </span>
      </label>
    </div>
  `
};

export const BFormInput = {
  props: ['modelValue', 'placeholder', 'type', 'disabled',
    'state', 'readonly'],
  emits: ['update:modelValue', 'input', 'blur'],
  template: `
    <input
      :type="type || 'text'"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      class="input input-bordered w-full"
      :class="{ 'input-error': state === false }"
      @input="$emit('update:modelValue', $event.target.value);
              $emit('input', $event.target.value)"
      @blur="$emit('blur', $event)"
    />
  `
};

export const BFormSelect = {
  props: ['modelValue', 'disabled'],
  emits: ['update:modelValue', 'change'],
  template: `
    <select class="select select-bordered w-full"
      :value="modelValue"
      :disabled="disabled"
      @change="$emit('update:modelValue', $event.target.value);
               $emit('change', $event.target.value)">
      <slot />
    </select>
  `
};

export const BFormSelectOption = {
  props: ['value', 'disabled'],
  template: `
    <option :value="value" :disabled="disabled">
      <slot />
    </option>
  `
};

export const BFormCheckbox = {
  props: {
    modelValue: {},
    value: { default: true },
    name: String,
    switch: Boolean,
    size: String,
    disabled: Boolean
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    isChecked() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.includes(this.value);
      }
      return this.modelValue === true ||
             this.modelValue === this.value;
    }
  },
  template: `
    <label class="flex items-center gap-2 cursor-pointer">
      <input
        v-if="!switch"
        type="checkbox"
        class="checkbox checkbox-primary"
        :checked="isChecked"
        :name="name"
        :disabled="disabled"
        @change="$emit('update:modelValue', $event.target.checked);
                 $emit('change', $event.target.checked)"
      />
      <input
        v-if="switch"
        type="checkbox"
        class="toggle toggle-primary"
        :checked="isChecked"
        :name="name"
        :disabled="disabled"
        @change="$emit('update:modelValue', $event.target.checked);
                 $emit('change', $event.target.checked)"
      />
      <slot />
    </label>
  `
};

export const BFormRadio = {
  props: ['modelValue', 'value', 'name', 'disabled'],
  emits: ['update:modelValue'],
  template: `
    <label class="flex items-center gap-2 cursor-pointer">
      <input type="radio"
        class="radio radio-primary"
        :name="name"
        :value="value"
        :checked="modelValue === value"
        :disabled="disabled"
        @change="$emit('update:modelValue', value)"
      />
      <slot />
    </label>
  `
};

export const BProgress = {
  props: ['value', 'max', 'variant', 'animated', 'striped'],
  template: `
    <progress class="progress w-full"
      :class="{
        'progress-success': variant === 'success',
        'progress-warning': variant === 'warning',
        'progress-error': variant === 'danger',
        'progress-info': variant === 'info',
        'progress-primary': !variant || variant === 'primary',
      }"
      :value="value || 0"
      :max="max || 100">
    </progress>
  `
};

export const BBreadcrumb = {
  props: ['items'],
  template: `
    <div class="breadcrumbs text-sm mb-4">
      <ul>
        <li v-for="item in (items || [])" :key="item.text">
          <router-link v-if="item.to" :to="item.to">
            {{ item.text }}
          </router-link>
          <span v-else>{{ item.text }}</span>
        </li>
      </ul>
    </div>
  `
};

export const BDropdown = {
  props: ['text', 'variant', 'size', 'right', 'noCaret'],
  template: `
    <div class="dropdown dropdown-end">
      <button tabindex="0" class="btn btn-ghost btn-sm gap-1">
        <slot name="button-content">{{ text }}</slot>
      </button>
      <ul tabindex="0"
        class="dropdown-content menu p-2 shadow-lg bg-base-100
        rounded-box w-52 z-50 border border-base-300">
        <slot />
      </ul>
    </div>
  `
};

export const BDropdownItem = {
  props: ['href', 'to'],
  template: `
    <li>
      <router-link v-if="to" :to="to"><slot /></router-link>
      <a v-else :href="href || '#'"><slot /></a>
    </li>
  `
};

export const BDropdownDivider = {
  template: `<li><hr class="my-1 border-base-300" /></li>`
};

export const BPagination = {
  props: ['modelValue', 'totalRows', 'perPage',
    'prevText', 'nextText'],
  emits: ['update:modelValue', 'page-click'],
  computed: {
    totalPages() {
      return Math.ceil(
        (this.totalRows || 0) / (this.perPage || 10)
      ) || 1;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.modelValue || 1;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (current <= 2) end = Math.min(total, 5);
      if (current >= total - 2) start = Math.max(1, total - 4);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  template: `
    <div class="flex items-center gap-1">
      <button class="btn btn-sm btn-ghost"
        :disabled="modelValue === 1"
        @click="$emit('update:modelValue', modelValue - 1);
                $emit('page-click', null, modelValue - 1)">
        {{ prevText || 'Prev' }}
      </button>
      <button v-for="page in visiblePages" :key="page"
        class="btn btn-sm btn-circle"
        :class="modelValue === page ? 'btn-neutral' : 'btn-ghost'"
        @click="$emit('update:modelValue', page);
                $emit('page-click', null, page)">
        {{ page }}
      </button>
      <button class="btn btn-sm btn-ghost"
        :disabled="modelValue === totalPages"
        @click="$emit('update:modelValue', modelValue + 1);
                $emit('page-click', null, modelValue + 1)">
        {{ nextText || 'Next' }}
      </button>
    </div>
  `
};

export const BTooltip = {
  props: ['title', 'triggers', 'placement'],
  template: `
    <div class="tooltip" :data-tip="title">
      <slot />
    </div>
  `
};

export const BFormTextarea = {
  props: ['modelValue', 'placeholder', 'disabled',
    'rows', 'state'],
  emits: ['update:modelValue'],
  template: `
    <textarea
      class="textarea textarea-bordered w-full"
      :class="{ 'textarea-error': state === false }"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows || 3"
      @input="$emit('update:modelValue', $event.target.value)">
      {{ modelValue }}
    </textarea>
  `
};
